import React, {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { alpha, Box, Portal, useTheme } from '@mui/material';

import { formatDistanceByCountry } from '@assets/helpers/utils/format-distance-by-country.ts';

import { useObservedWidth } from '../../assets/helpers/hooks/use-observed-width.ts';
import { Button } from '../../components/button/index.ts';

export type IsoDate = string;

export type FlightGraphicPoint = {
  date: IsoDate;
  x: number;
  y: number;
  z: number;
};

type FlightGraphicProps = {
  data: FlightGraphicPoint[];
  height?: number;
  pointDurationMs?: number;
  headroom?: number;
  autoPlay?: boolean;
  showToggleButton?: boolean;
  buttonLabel?: string;
  className?: string;
  countryIsoCode?: string | null;
};

type Point2D = {
  x: number;
  y: number;
};

type FlightPathLayout = {
  viewBoxWidth: number;
  viewBoxHeight: number;
  centerX: number;
  fieldTopY: number;
  fieldBottomY: number;
  fieldHeight: number;
  fieldTopWidth: number;
  fieldBottomWidth: number;
  leftAxisX: number;
  labelX: number;
};

const DEFAULT_HEIGHT = 310;
const DEFAULT_HEADROOM = 50;
const DEFAULT_POINT_DURATION_MS = 180;
const DEFAULT_TICK_COUNT = 4;
const STRIPES_COUNT = 8;

const BACKGROUND_COLOR = '#F2F8ED';
const FIELD_DARK = '#CFDEC1';
const FIELD_LIGHT = '#E5F0DB';

const LATERAL_SPREAD_RATIO = 0.18;
const ELEVATION_PIXELS_PER_METER = 32;
const MAX_ELEVATION_LIFT_RATIO = 0.34;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const lerp = (from: number, to: number, t: number) => from + (to - from) * t;

const getDistanceScaleConfig = (
  data: FlightGraphicPoint[],
  tickCount: number,
  headroom: number
) => {
  const maxDistance = data.reduce((acc, point) => Math.max(acc, point.z), 0);
  const scaleMax = Math.max(1, Math.round(maxDistance + headroom));

  return {
    scaleMax,
    ticks: Array.from({ length: tickCount + 1 }, (_, index) =>
      index === tickCount ? scaleMax : Math.round((scaleMax * index) / tickCount)
    ),
  };
};

const getProgressRatio = (progress: number, pointsCount: number) => {
  if (pointsCount <= 1) return 0;
  return clamp(progress / (pointsCount - 1), 0, 1);
};

const getMaxAbsX = (points: FlightGraphicPoint[]) => {
  return points.reduce((acc, point) => Math.max(acc, Math.abs(point.x)), 0);
};

/**
 * Builds a path from the API's discrete trajectory points without smoothing them.
 * Smoothing would alter the measured flight and can hide the landing-to-roll transition.
 */
const buildPathFromPoints = (points: Point2D[]) => {
  if (!points.length) return '';

  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
};

const projectFlightPointTo2D = ({
  point,
  layout,
  distanceScaleMax,
  maxAbsX,
  getHalfFieldWidthByRatio,
}: {
  point: FlightGraphicPoint;
  layout: FlightPathLayout;
  distanceScaleMax: number;
  maxAbsX: number;
  getHalfFieldWidthByRatio: (ratio: number) => number;
}): Point2D => {
  const depthRatio = distanceScaleMax > 0 ? clamp(point.z / distanceScaleMax, 0, 1) : 0;
  const lateralRatio = maxAbsX > 0 ? clamp(point.x / maxAbsX, -1, 1) : 0;

  const halfWidthAtDepth = getHalfFieldWidthByRatio(depthRatio);

  const projectedX = layout.centerX + lateralRatio * halfWidthAtDepth * LATERAL_SPREAD_RATIO;

  const depthOffsetY = depthRatio * layout.fieldHeight;
  const heightLiftY = Math.min(
    Math.max(point.y, 0) * ELEVATION_PIXELS_PER_METER,
    layout.fieldHeight * MAX_ELEVATION_LIFT_RATIO
  );

  const projectedY = clamp(
    layout.fieldBottomY - depthOffsetY - heightLiftY,
    layout.fieldTopY + 6,
    layout.fieldBottomY
  );

  return {
    x: projectedX,
    y: projectedY,
  };
};

export const FlightGraphic = memo((props: FlightGraphicProps) => {
  const {
    data,
    height = DEFAULT_HEIGHT,
    pointDurationMs = DEFAULT_POINT_DURATION_MS,
    headroom = DEFAULT_HEADROOM,
    autoPlay = false,
    showToggleButton = true,
    buttonLabel = 'Activate Radar',
    className,
    countryIsoCode,
  } = props;

  const { palette, typography } = useTheme();
  const { ref: containerRef, width: containerWidth } = useObservedWidth<HTMLDivElement>();

  const TRAJECTORY_COLOR = palette.accent_text;
  const GRID_COLOR = palette.light_dark;

  const clipPathId = useId().replace(/:/g, '_');
  const pathRef = useRef<SVGPathElement | null>(null);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [data]);

  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const progressRatioRef = useRef(0);
  const animationStartRef = useRef<number | null>(null);

  useEffect(() => {
    setIsPlaying(autoPlay);
  }, [autoPlay]);

  useEffect(() => {
    if (!sortedData.length) {
      setProgress(0);
      return;
    }

    if (sortedData.length === 1) {
      setProgress(0);
      return;
    }

    if (!isPlaying) return;

    const segmentCount = sortedData.length - 1;
    const cycleDuration = segmentCount * pointDurationMs;

    let frameId = 0;

    const animate = (now: number) => {
      if (animationStartRef.current === null) {
        animationStartRef.current = now - progressRatioRef.current * cycleDuration;
      }

      const elapsed = now - animationStartRef.current;
      const nextRatio = (elapsed % cycleDuration) / cycleDuration;
      const nextProgress = nextRatio * segmentCount;

      setProgress(nextProgress);
      frameId = window.requestAnimationFrame(animate);
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isPlaying, pointDurationMs, sortedData]);

  const layout = useMemo<FlightPathLayout>(() => {
    const viewBoxWidth = Math.max(containerWidth, 320);
    const viewBoxHeight = height;
    const centerX = viewBoxWidth / 2;

    const fieldTopY = 22;
    const fieldBottomY = viewBoxHeight - 10;
    const fieldHeight = fieldBottomY - fieldTopY;

    const fieldBottomWidth = clamp(viewBoxWidth * 0.66, 260, viewBoxWidth - 84);
    const fieldTopWidth = clamp(viewBoxWidth * 0.24, 140, fieldBottomWidth - 100);

    return {
      viewBoxWidth,
      viewBoxHeight,
      centerX,
      fieldTopY,
      fieldBottomY,
      fieldHeight,
      fieldTopWidth,
      fieldBottomWidth,
      leftAxisX: 52,
      labelX: 18,
    };
  }, [containerWidth, height]);

  const distanceScaleConfig = useMemo(() => {
    return getDistanceScaleConfig(sortedData, DEFAULT_TICK_COUNT, headroom);
  }, [sortedData, headroom]);

  const maxAbsX = useMemo(() => getMaxAbsX(sortedData), [sortedData]);

  const getHalfFieldWidthByRatio = useCallback(
    (ratio: number) => {
      const width = lerp(layout.fieldBottomWidth, layout.fieldTopWidth, ratio);
      return width / 2;
    },
    [layout.fieldBottomWidth, layout.fieldTopWidth]
  );

  const projectedPoints = useMemo(() => {
    return sortedData.map((point) =>
      projectFlightPointTo2D({
        point,
        layout,
        distanceScaleMax: distanceScaleConfig.scaleMax,
        maxAbsX,
        getHalfFieldWidthByRatio,
      })
    );
  }, [sortedData, layout, distanceScaleConfig.scaleMax, getHalfFieldWidthByRatio, maxAbsX]);

  const hoveredPoint = useMemo(() => {
    if (hoveredIndex == null) return null;

    const point = sortedData[hoveredIndex];
    const screen = projectedPoints[hoveredIndex];

    return point && screen ? { point, screen } : null;
  }, [hoveredIndex, projectedPoints, sortedData]);

  const tooltipPosition = useMemo(() => {
    if (!hoveredPoint || !containerRef.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();

    return {
      left:
        containerRect.left + (hoveredPoint.screen.x / layout.viewBoxWidth) * containerRect.width,
      top:
        containerRect.top + (hoveredPoint.screen.y / layout.viewBoxHeight) * containerRect.height,
    };
  }, [containerRef, hoveredPoint, layout.viewBoxHeight, layout.viewBoxWidth]);

  const pathD = useMemo(() => {
    return buildPathFromPoints(projectedPoints);
  }, [projectedPoints]);

  const progressRatio = useMemo(() => {
    return getProgressRatio(progress, sortedData.length);
  }, [progress, sortedData.length]);

  const fallbackBallPoint = useMemo(
    () =>
      projectedPoints[0] ?? {
        x: layout.centerX,
        y: layout.fieldBottomY,
      },
    [layout.centerX, layout.fieldBottomY, projectedPoints]
  );

  const [currentBallPoint, setCurrentBallPoint] = useState<Point2D>(fallbackBallPoint);

  useLayoutEffect(() => {
    const pathNode = pathRef.current;

    if (!pathNode || !pathD) {
      setCurrentBallPoint((currentPoint) =>
        currentPoint.x === fallbackBallPoint.x && currentPoint.y === fallbackBallPoint.y
          ? currentPoint
          : fallbackBallPoint
      );
      return;
    }

    const totalLength = pathNode.getTotalLength();
    const pointAtLength = pathNode.getPointAtLength(totalLength * progressRatio);

    const nextBallPoint = {
      x: pointAtLength.x,
      y: pointAtLength.y,
    };

    setCurrentBallPoint((currentPoint) =>
      currentPoint.x === nextBallPoint.x && currentPoint.y === nextBallPoint.y
        ? currentPoint
        : nextBallPoint
    );
  }, [pathD, progressRatio, fallbackBallPoint]);

  const handleTogglePlayback = () => {
    if (sortedData.length <= 1) return;

    const segmentCount = sortedData.length - 1;
    progressRatioRef.current = segmentCount > 0 ? progress / segmentCount : 0;
    animationStartRef.current = null;
    setIsPlaying((prev) => !prev);
  };

  const handleSvgMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const pointer = {
      x: ((event.clientX - rect.left) / rect.width) * layout.viewBoxWidth,
      y: ((event.clientY - rect.top) / rect.height) * layout.viewBoxHeight,
    };
    const snapRadius = 28;
    const snapRadiusSquared = snapRadius ** 2;
    let closestIndex: number | null = null;
    let closestDistanceSquared = snapRadiusSquared;

    projectedPoints.forEach((point, index) => {
      const distanceSquared = (point.x - pointer.x) ** 2 + (point.y - pointer.y) ** 2;

      if (distanceSquared <= closestDistanceSquared) {
        closestIndex = index;
        closestDistanceSquared = distanceSquared;
      }
    });

    setHoveredIndex((currentIndex) =>
      currentIndex === closestIndex ? currentIndex : closestIndex
    );
  };

  if (!sortedData.length) {
    return (
      <Box
        className={className}
        sx={{
          width: '100%',
          height,
          borderRadius: 2,
          bgcolor: BACKGROUND_COLOR,
        }}
      />
    );
  }

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: BACKGROUND_COLOR,
      }}
    >
      {showToggleButton && (
        <Button
          variantStyle="secondary"
          size="small"
          onClick={handleTogglePlayback}
          sx={{
            position: 'absolute',
            top: 14,
            right: 18,
            zIndex: 2,
            width: '102px',
          }}
        >
          {isPlaying ? 'Pause Radar' : buttonLabel}
        </Button>
      )}

      <svg
        width={layout.viewBoxWidth}
        height={layout.viewBoxHeight}
        viewBox={`0 0 ${layout.viewBoxWidth} ${layout.viewBoxHeight}`}
        style={{ display: 'block' }}
        onMouseMove={handleSvgMouseMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <defs>
          <clipPath id={clipPathId}>
            <path
              d={`
                M ${layout.centerX - layout.fieldTopWidth / 2} ${layout.fieldTopY}
                L ${layout.centerX + layout.fieldTopWidth / 2} ${layout.fieldTopY}
                L ${layout.centerX + layout.fieldBottomWidth / 2} ${layout.fieldBottomY}
                L ${layout.centerX - layout.fieldBottomWidth / 2} ${layout.fieldBottomY}
                Z
              `}
            />
          </clipPath>
        </defs>

        <g clipPath={`url(#${clipPathId})`}>
          {Array.from({ length: STRIPES_COUNT }).map((_, index) => {
            const stripeHeight = layout.fieldHeight / STRIPES_COUNT;
            const stripeY = layout.fieldTopY + index * stripeHeight;

            return (
              <rect
                key={index}
                x={layout.centerX - layout.fieldBottomWidth / 2 - 30}
                y={stripeY}
                width={layout.fieldBottomWidth + 60}
                height={stripeHeight}
                fill={index % 2 === 0 ? FIELD_DARK : FIELD_LIGHT}
              />
            );
          })}

          {pathD && (
            <path
              ref={pathRef}
              d={pathD}
              fill="none"
              stroke={TRAJECTORY_COLOR}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          )}

          <circle
            cx={currentBallPoint.x}
            cy={currentBallPoint.y}
            r="8"
            fill={palette.white_100}
            stroke={TRAJECTORY_COLOR}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />

          {hoveredPoint && (
            <circle
              cx={hoveredPoint.screen.x}
              cy={hoveredPoint.screen.y}
              r="5.5"
              fill={palette.white_100}
              stroke={TRAJECTORY_COLOR}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </g>

        {distanceScaleConfig.ticks.map((tickValue) => {
          const ratio = tickValue / distanceScaleConfig.scaleMax;
          const y = layout.fieldBottomY - ratio * layout.fieldHeight;
          const fieldLeftX = layout.centerX - getHalfFieldWidthByRatio(ratio);

          return (
            <g key={tickValue}>
              <text
                x={layout.labelX}
                y={y + 5}
                fill={GRID_COLOR}
                fontFamily={typography.fontFamily}
                fontSize="14"
                fontWeight={400}
              >
                {Math.round(tickValue)}
              </text>

              <line
                x1={layout.leftAxisX}
                y1={y}
                x2={fieldLeftX - 10}
                y2={y}
                stroke={GRID_COLOR}
                strokeDasharray="4 6"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>

      {hoveredPoint && tooltipPosition && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              left: tooltipPosition.left,
              top: tooltipPosition.top,
              zIndex: (theme) => theme.zIndex.tooltip,
              display: 'grid',
              gap: 0.5,
              px: 1.5,
              py: 0.8,
              borderRadius: '14px',
              bgcolor: palette.white_100,
              border: `1px solid ${alpha(palette.accent_text, 0.35)}`,
              boxShadow: `0 8px 24px ${palette.black_15}`,
              color: palette.dark,
              fontSize: 16,
              lineHeight: 1,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              transform: 'translate(-50%, calc(-100% - 12px))',
            }}
          >
            <Box component="span">
              Distance: {formatDistanceByCountry(countryIsoCode, hoveredPoint.point.z)}
            </Box>
            <Box component="span">
              Height: {formatDistanceByCountry(countryIsoCode, hoveredPoint.point.y, 2)}
            </Box>
            <Box component="span">
              Side: {hoveredPoint.point.x < 0 ? 'L' : 'R'}{' '}
              {formatDistanceByCountry(countryIsoCode, Math.abs(hoveredPoint.point.x), 2)}
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  );
});

FlightGraphic.displayName = 'FlightGraphic';
